import { useApp } from '@/contexts';

import { useState } from 'react';

import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetWizardTypesQuery } from '@/features/wizards/api/wizardsApi';

/**
 * New Wizard Page - Select source and destination connectors
 * Based on dataProtect newWizard.js
 *
 * Shows ALL available wizard types (not just configured credentials)
 */
export const NewWizardPage = () => {
  const { appId } = useApp();
  const navigate = useNavigate();

  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);

  const { data: wizardTypes = [], isLoading } = useGetWizardTypesQuery();

  // Extract unique source and destination types
  const sources = Array.from(new Set(wizardTypes.map((w: any) => w.sourceType))).sort();
  const destinations = Array.from(new Set(wizardTypes.map((w: any) => w.destinationType))).sort();

  // Check if selected combination is valid
  const isValidCombination = (source: string | null, destination: string | null) => {
    if (!source || !destination) return true;
    return wizardTypes.some(
      (w: any) => w.sourceType === source && w.destinationType === destination
    );
  };

  const handleNext = () => {
    if (!selectedSource || !selectedDestination) return;

    const wizardType = wizardTypes.find(
      (w: any) => w.sourceType === selectedSource && w.destinationType === selectedDestination
    );

    if (!wizardType) return;

    // Navigate to wizard form with wizard type ID
    navigate(`/a/${appId}/wiz/start/${wizardType.typeId}`);
  };

  const handleCancel = () => {
    navigate(`/a/${appId}/tf`);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title="Create New Wizard"
        subtitle="Select a source and destination to create a guided integration"
      />

      {/* Selected connectors preview */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4, gap: 2 }}>
        <Box
          sx={{
            width: 90,
            height: 90,
            border: '2px dashed',
            borderColor: 'grey.500',
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: selectedSource ? 'background.paper' : 'transparent',
          }}
        >
          {selectedSource && (
            <Typography variant="body2" sx={{ textAlign: 'center', p: 1 }}>
              {selectedSource}
            </Typography>
          )}
        </Box>
        <ArrowIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Box
          sx={{
            width: 90,
            height: 90,
            border: '2px dashed',
            borderColor: 'grey.500',
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: selectedDestination ? 'background.paper' : 'transparent',
          }}
        >
          {selectedDestination && (
            <Typography variant="body2" sx={{ textAlign: 'center', p: 1 }}>
              {selectedDestination}
            </Typography>
          )}
        </Box>
        <Box sx={{ ml: 2 }}>
          <Button variant="outlined" onClick={handleCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              !selectedSource ||
              !selectedDestination ||
              !isValidCombination(selectedSource, selectedDestination)
            }
          >
            Launch Wizard
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Source Selection */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
          >
            Data sources
          </Typography>
          <Grid container spacing={2}>
            {sources.length === 0 ? (
              <Grid item xs={12}>
                <Typography sx={{ p: '2px 16px', fontSize: '14px' }}>
                  Your Organization does not have any Integration Wizards enabled. Please contact
                  Lingk sales to purchase Lingk Speed Wizards.
                </Typography>
              </Grid>
            ) : (
              sources.map((sourceType: string) => {
                const isDisabled =
                  selectedDestination && !isValidCombination(sourceType, selectedDestination);

                return (
                  <Grid item xs={12} sm={6} key={sourceType}>
                    <Card
                      sx={{
                        cursor: isDisabled ? 'default' : 'pointer',
                        border: 2,
                        borderColor: selectedSource === sourceType ? 'primary.main' : 'divider',
                        opacity: isDisabled ? 0.5 : 1,
                        '&:hover': isDisabled ? {} : { borderColor: 'primary.light', boxShadow: 2 },
                      }}
                      onClick={() => !isDisabled && setSelectedSource(sourceType)}
                    >
                      <CardContent sx={{ textAlign: 'center', minHeight: 100 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sourceType}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Grid>

        {/* Destination Selection */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
          >
            Data targets
          </Typography>
          <Grid container spacing={2}>
            {destinations.length === 0 ? (
              <Grid item xs={12}>
                <Typography sx={{ p: '2px 16px', fontSize: '14px' }}>
                  No destination connectors available.
                </Typography>
              </Grid>
            ) : (
              destinations.map((destinationType: string) => {
                const isDisabled =
                  selectedSource && !isValidCombination(selectedSource, destinationType);

                return (
                  <Grid item xs={12} sm={6} key={destinationType}>
                    <Card
                      sx={{
                        cursor: isDisabled ? 'default' : 'pointer',
                        border: 2,
                        borderColor:
                          selectedDestination === destinationType ? 'secondary.main' : 'divider',
                        opacity: isDisabled ? 0.5 : 1,
                        '&:hover': isDisabled
                          ? {}
                          : { borderColor: 'secondary.light', boxShadow: 2 },
                      }}
                      onClick={() => !isDisabled && setSelectedDestination(destinationType)}
                    >
                      <CardContent sx={{ textAlign: 'center', minHeight: 100 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {destinationType}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Grid>
      </Grid>
    </PageContainer>
  );
};
